import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Calendar, MapPin, Star, Sparkles, ChevronLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BottomNav from "@/components/app/BottomNav";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const formatKickoff = (iso) => {
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) +
      (iso.includes("T") ? " · " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "");
  } catch { return iso; }
};

export default function WorldCup() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingOptIn, setSavingOptIn] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/worldcup/summary");
      setSummary(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load World Cup fixtures");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleOptIn = async (val) => {
    setSavingOptIn(true);
    try {
      await api.post("/worldcup/opt-in-england", { opt_in: val });
      await refresh();
      await load();
      toast.success(val ? "You're in — we'll boost you on England match days" : "Opted out");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setSavingOptIn(false); }
  };

  return (
    <div className="min-h-screen bg-footy-bg text-white pb-24 grain">
      {/* Hero */}
      <header className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 opacity-30" style={{
          background: "radial-gradient(circle at 20% 20%, #d4af37 0%, transparent 40%), radial-gradient(circle at 80% 80%, #e63946 0%, transparent 40%)"
        }} />
        <div className="max-w-md mx-auto px-5 pt-6 pb-8 relative">
          <button onClick={()=>nav(-1)} className="text-slate-400 hover:text-white inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest mb-6" data-testid="wc-back">
            <ChevronLeft size={14} /> Back
          </button>
          <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.35em] text-yellow-400 mb-3">
            <Trophy size={12} /> World Cup 2026 · USA · CAN · MEX
          </div>
          <h1 className="font-display text-5xl uppercase leading-[0.9] tracking-tight">Every game.<br />Every fan.</h1>
          <p className="text-slate-300 mt-4 text-sm leading-relaxed max-w-sm">
            Six weeks of daily football. Check in when you&apos;re at the pub, at the ground, or hosting the game at home — Footymatch surfaces every other fan doing the same.
          </p>
          <div className="mt-6 grid grid-cols-3 gap-2 text-center">
            <div className="footy-card p-3">
              <div className="font-display text-2xl text-yellow-400" data-testid="wc-count-total">{summary?.total_fixtures ?? "—"}</div>
              <div className="text-[9px] text-slate-400 uppercase tracking-widest">Fixtures</div>
            </div>
            <div className="footy-card p-3">
              <div className="font-display text-2xl text-yellow-400">3</div>
              <div className="text-[9px] text-slate-400 uppercase tracking-widest">Host nations</div>
            </div>
            <div className="footy-card p-3">
              <div className="font-display text-2xl text-yellow-400">32</div>
              <div className="text-[9px] text-slate-400 uppercase tracking-widest">Teams</div>
            </div>
          </div>
        </div>
      </header>

      {loading ? (
        <div className="max-w-md mx-auto px-5 pt-5 space-y-3">{[1,2,3].map(i => <div key={i} className="h-32 rounded-2xl bg-white/5 animate-pulse" />)}</div>
      ) : (
        <>
          {/* England fixtures — pinned */}
          {(summary?.england_fixtures || []).length > 0 && (
            <section className="max-w-md mx-auto px-5 pt-6" data-testid="wc-england-section">
              <div className="flex items-center gap-2 mb-3">
                <Star size={14} className="text-footy-red" fill="currentColor" />
                <h2 className="font-display text-xl uppercase tracking-wide">England's road</h2>
              </div>
              <div className="space-y-3">
                {summary.england_fixtures.map(f => (
                  <EnglandCard key={f.id} f={f} onCheckin={()=>nav("/fixtures")} />
                ))}
              </div>

              {/* Opt-in banner */}
              <div className="mt-4 footy-card p-4 flex items-start gap-3 border-yellow-500/30" data-testid="wc-opt-in-card">
                <div className="w-10 h-10 rounded-xl bg-yellow-500/15 text-yellow-400 flex items-center justify-center flex-none">
                  <Sparkles size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">England match-day boost</div>
                  <p className="text-xs text-slate-400 mt-1">
                    Get surfaced first in Discover when England play. More fans see you on the day it matters most.
                  </p>
                </div>
                <Switch
                  data-testid="wc-opt-in-toggle"
                  checked={!!summary?.auto_rsvp_england}
                  onCheckedChange={toggleOptIn}
                  disabled={savingOptIn}
                />
              </div>
            </section>
          )}

          {/* Rest of tournament */}
          <section className="max-w-md mx-auto px-5 pt-6" data-testid="wc-other-section">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={14} className="text-slate-400" />
              <h2 className="font-display text-xl uppercase tracking-wide">More fixtures</h2>
            </div>
            <div className="space-y-3">
              {(summary?.other_fixtures || []).map(f => (
                <MiniFixture key={f.id} f={f} />
              ))}
            </div>
            {(summary?.other_fixtures || []).length === 0 && (
              <div className="footy-card p-6 text-center text-sm text-slate-400">No other fixtures scheduled yet.</div>
            )}
            <div className="mt-6 text-center">
              <Link to="/fixtures" className="text-xs uppercase tracking-widest text-slate-400 hover:text-white font-bold" data-testid="wc-see-all">
                See all fixtures →
              </Link>
            </div>
          </section>
        </>
      )}

      <BottomNav />
    </div>
  );
}

function EnglandCard({ f, onCheckin }) {
  const isEnglandHome = (f.home_team || "").toLowerCase() === "england";
  const opponent = isEnglandHome ? f.away_team : f.home_team;
  return (
    <motion.div className="footy-card overflow-hidden relative" data-testid={`wc-england-${f.id}`}
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
        background: "linear-gradient(120deg, #e63946 0%, transparent 60%)"
      }} />
      <div className="p-5 relative">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-yellow-400 mb-2">
          {f.matchday ? `Matchday ${f.matchday}` : "Group stage"}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="font-display text-3xl uppercase tracking-tight leading-none">England</div>
            <div className="text-xs text-slate-400 uppercase tracking-widest mt-1">vs {opponent}</div>
          </div>
          <button onClick={onCheckin}
            className="text-[10px] font-bold uppercase tracking-widest px-3 py-2 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
            data-testid={`wc-england-checkin-${f.id}`}>
            Check-in ›
          </button>
        </div>
        <div className="mt-4 flex items-center gap-4 text-xs text-slate-300">
          <div className="flex items-center gap-1.5"><Calendar size={12} className="text-footy-red" /> {formatKickoff(f.kickoff)}</div>
          {f.venue && <div className="flex items-center gap-1.5"><MapPin size={12} className="text-footy-red" /> {f.venue}</div>}
        </div>
      </div>
    </motion.div>
  );
}

function MiniFixture({ f }) {
  const home = f.home_team_short || f.home_team;
  const away = f.away_team_short || f.away_team;
  return (
    <div className="footy-card p-3 flex items-center gap-3" data-testid={`wc-mini-${f.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-display text-lg uppercase tracking-wide leading-tight truncate">
          {home} <span className="text-slate-500 text-sm">vs</span> {away}
        </div>
        <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">{formatKickoff(f.kickoff)}</div>
      </div>
      {f.venue && <div className="text-[10px] text-slate-500 truncate max-w-[100px]">{f.venue}</div>}
    </div>
  );
}
